import React from "react";
import { Button, Card, ListGroup, Row, Col } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import AppComponent from "../../../../Components/AppComponent";
import { getFullName } from "../../../../Modules/utils/fullname";
import SummaryCardLayout from "./SummaryCardLayout";
import { RenderDenomination } from "../../../../Components/etc/RenderAmount";
import UserBalanceSnapshot from "./UserBalanceSnapshot";
import EditableTransactionNote from "./EditableNote";

export default class FinalSummaryAgentReport extends AppComponent {
  componentRef = React.createRef();

  renderPreview() {
    const d = this.props.data;

    const totalCOD = Number(d.cod.totalCOD || 0);

    // COD paid by FE
    const depositedCash = Number(d.totalAmountDetails.cashAmount || 0);
    const depositedOnline = Number(d.totalAmountDetails.onlineAmount || 0);
    const totalDeposit = Number(d.totalAmount || 0);

    // Current balance due delta
    const balanceDueDelta = Number((totalCOD - totalDeposit).toFixed(2));

    // commission
    const deliveryCount = Number(d.cod.deliveryCount || 0);
    const pickupCount = Number(d.cod.pickupCount || 0);
    const deliveryRate = Number(d.cod.deliveryRate || 0);
    const pickupRate = Number(d.cod.pickupRate || 0);

    const fad = d.cod.fad;

    const currentCommission =
      deliveryCount * deliveryRate + pickupCount * pickupRate;

    const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

    return (
      <div>
        <ListGroup variant="flush">
          {/* BASIC INFO */}
          <ListGroup.Item className="py-1 px-0">
            <Row>
              <Col>Field Executive:</Col>
              <Col className="text-end fw-bold">
                {getFullName(d.agent || d.relatedUser)}
              </Col>
            </Row>
          </ListGroup.Item>

          {/* COD SUMMARY                  */}
          <ListGroup.Item className="bg-light fw-semibold small py-2 px-0 mt-3">
            COD SUMMARY
          </ListGroup.Item>

          <ListGroup.Item className="py-1 px-0">
            <Row>
              <Col>Total COD Amount:</Col>
              <Col className="text-end fw-bold">₹{totalCOD}</Col>
            </Row>
          </ListGroup.Item>
          <ListGroup.Item className="py-1 px-0">
            <Row>
              <Col>Deliveries: (Rate ₹{deliveryRate})</Col>
              <Col className="text-end">{deliveryCount}</Col>
            </Row>
          </ListGroup.Item>

          <ListGroup.Item className="py-1 px-0">
            <Row>
              <Col>Pickups: (Rate ₹{pickupRate})</Col>
              <Col className="text-end">{pickupCount}</Col>
            </Row>
          </ListGroup.Item>

          <ListGroup.Item className="py-1 px-0">
            <Row>
              <Col>FAD</Col>
              <Col className="text-end">{fad}%</Col>
            </Row>
          </ListGroup.Item>

          {/* COD → OFFICE                 */}
          <ListGroup.Item className="bg-light fw-semibold small py-2 px-0 mt-3">
            Total Amount Deposited by FE
          </ListGroup.Item>

          <ListGroup.Item className="py-1 px-0">
            <Row>
              <Col>Cash:</Col>
              <Col className="text-end">₹{depositedCash}</Col>
            </Row>
          </ListGroup.Item>

          {d.totalAmountDetails.cashDenominations ? (
            <ListGroup.Item className="py-1 px-0">
              <Row>
                <Col>Denominations:</Col>
                <Col className="text-end text-muted small">
                  <RenderDenomination
                    denominations={d.totalAmountDetails.cashDenominations}
                  />
                </Col>
              </Row>
            </ListGroup.Item>
          ) : null}

          <ListGroup.Item className="py-1 px-0">
            <Row>
              <Col>Online:</Col>
              <Col className="text-end">₹{depositedOnline}</Col>
            </Row>
          </ListGroup.Item>

          <ListGroup.Item className="py-1 px-0">
            <Row>
              <Col>Total Deposit:</Col>
              <Col className="text-end fw-bold">₹{totalDeposit}</Col>
            </Row>
          </ListGroup.Item>

          {/* BALANCE SUMMARY (PREVIEW)     */}
          <ListGroup.Item className="bg-light fw-semibold small py-2 px-0 mt-3">
            Balance Due Calculation
          </ListGroup.Item>

          {/* COD Balance Full Formula */}
          <ListGroup.Item className="py-2 px-0">
            <Row className="small">
              <Col>Total COD Amount</Col>
              <Col className="text-end">₹{totalCOD}</Col>
            </Row>

            <Row className="small">
              <Col>– Total Amount Deposited</Col>
              <Col className="text-end">(-) ₹{totalDeposit}</Col>
            </Row>

            <Row className="mt-1 pt-1 border-top fw-bold">
              <Col>Current Balance Due</Col>
              <Col className="text-end">₹{balanceDueDelta}</Col>
            </Row>
          </ListGroup.Item>

          {/* Commission full breakdown */}
          <ListGroup.Item className="py-2 px-0">
            <div className="fw-semibold mb-1">Commission Earned</div>

            <Row className="small">
              <Col>
                Delivery ({deliveryCount} × ₹{deliveryRate})
              </Col>
              <Col className="text-end">₹{deliveryCount * deliveryRate}</Col>
            </Row>

            <Row className="small">
              <Col>
                Pickup ({pickupCount} × ₹{pickupRate})
              </Col>
              <Col className="text-end">₹{pickupCount * pickupRate}</Col>
            </Row>

            <Row className="mt-1 pt-1 border-top fw-bold">
              <Col>Current Commission</Col>
              <Col className="text-end">₹{currentCommission}</Col>
            </Row>
          </ListGroup.Item>
          <EditableTransactionNote transaction={d} />

          <UserBalanceSnapshot
            key={d?._id}
            userId={d?.agent?._id || d?.relatedUser?._id}
            title="Latest User Balances"
          />
        </ListGroup>
      </div>
    );
  }

  renderPrintable() {
    const d = this.props.data?.transaction || this.props.data || {};

    const userBalCurrent = d.userBalanceDue?.current ?? null;
    const userBalTotal = d.userBalanceDue?.total ?? null;

    const totalCOD = Number(d.cod.totalCOD || 0);
    const totalDeposit = Number(d.totalAmount || 0);

    // Local Commission
    const deliveryCount = Number(d.cod.deliveryCount || 0);
    const pickupCount = Number(d.cod.pickupCount || 0);

    // Utility formatters
    const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

    return (
      <div>
        {/* FE DETAILS */}
        <h6 className="fw-bold mb-2">Field Executive Report</h6>
        <table className="table table-borderless table-sm mb-3">
          <tbody>
            <tr>
              <td>Field Executive</td>
              <td className="text-end fw-bold">{getFullName(d.relatedUser)}</td>
            </tr>
            <tr>
              <td>Deliveries</td>
              <td className="text-end">{deliveryCount}</td>
            </tr>
            <tr>
              <td>Pickups</td>
              <td className="text-end">{pickupCount}</td>
            </tr>
            <tr>
              <td>FAD %</td>
              <td className="text-end">{d.cod.fad ? d.cod.fad + "%" : "-"}</td>
            </tr>
          </tbody>
        </table>

        {/* COD SUMMARY */}
        <h6 className="fw-bold">Financial Summary</h6>
        <table className="table table-sm mb-3">
          <tbody>
            <tr>
              <td>Total COD Amount</td>
              <td className="text-end fw-bold">{INR(totalCOD)}</td>
            </tr>

            <tr>
              <td className="text-muted">Cash Paid by FE</td>
              <td className="text-end text-muted">
                {INR(Number(d.totalAmountDetails?.cashAmount))}
              </td>
            </tr>
            {d.totalAmountDetails.cashDenominations ? (
              <tr>
                <td className="text-muted">Denominations</td>
                <td className="text-end text-muted small">
                  <RenderDenomination
                    denominations={d.totalAmountDetails.cashDenominations}
                  />
                </td>
              </tr>
            ) : null}
            <tr>
              <td className="text-muted">Online Paid by FE</td>
              <td className="text-end text-muted">
                {INR(Number(d.totalAmountDetails?.onlineAmount))}
              </td>
            </tr>

            <tr>
              <td>Total Amount Paid by FE</td>
              <td className="text-end fw-bold">{INR(totalDeposit)}</td>
            </tr>

            {/* Balance formula line */}
            <tr className="table-light">
              <td colSpan={2}>
                <small className="text-muted">
                  Balance Due = Total COD – Paid Amount
                </small>
              </td>
            </tr>

            <tr>
              <td>Current Balance Due</td>
              <td className="text-end fw-bold">{INR(userBalCurrent)}</td>
            </tr>

            {userBalTotal != null && (
              <tr>
                <td>Total Balance Due (Overall)</td>
                <td className="text-end">{INR(userBalTotal)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const props = this.props;
    const d = this.props.data;
    let mode = "preview";
    if (this.props.mode === "details") mode = "details";
    else if (this.props.data?.transaction?._id) mode = "submitted";

    return (
      <SummaryCardLayout
        mode={mode}
        data={d}
        renderPreviewContent={this.renderPreview.bind(this)}
        renderPrintableContent={this.renderPrintable.bind(this)}
        renderSummaryContent={() => this.renderPreview()}
        onBack={() => props.onSubmit({ step: d.step - 1 })}
        onSubmit={props.onFinalSubmit}
        onClose={props.handleClose}
        onEdit={props.handleEdit}
        onDelete={props.handleDelete}
        onApprove={props.handleApprove}
      />
    );
  }
}
